import { useCallback, useState } from 'react'

import { getSalesLensItems } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { IAbstractLensItem } from '@/types'
import { SalesLensItemsModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'
import { DateRange } from 'react-day-picker'

export default function useSalesLensItems(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [itemsData, setItemsData] = useState<
    Map<string, SalesLensItemsModel[]>
  >(new Map())
  const [trendItemsData, setTrendItemsData] = useState<
    Map<string, SalesLensItemsModel[]>
  >(new Map())

  const fetchItemsForCategory = useCallback(
    async (subSubCategory: string): Promise<SalesLensItemsModel[]> => {
      // Return cached data if available
      if (itemsData.has(subSubCategory)) {
        return itemsData.get(subSubCategory)!
      }

      setLoading(true)
      setError(null)

      try {
        const [currentData, trendData] = await Promise.all([
          fetchItemsData(branches, startDate, endDate, subSubCategory),
          fetchTrendItemsData(branches, subSubCategory),
        ])

        // Update the cache with new data
        setItemsData(
          (prev) => new Map(prev.set(subSubCategory, currentData.data))
        )
        setTrendItemsData(
          (prev) => new Map(prev.set(subSubCategory, trendData.data))
        )

        return currentData.data
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred'
        setError(new Error(errorMessage))
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [branches, startDate, endDate, itemsData]
  )

  const fetchTrendItemsData = useCallback(
    async (
      branches: string,
      subSubCategory: string
    ): Promise<{ data: SalesLensItemsModel[] }> => {
      const trendingDate = getTrendingDate(
        { from: new Date(startDate), to: new Date(endDate) },
        dateFilterType
      ) as DateRange
      return await fetchItemsData(
        branches,
        dateToString(trendingDate, 'from'),
        dateToString(trendingDate, 'to'),
        subSubCategory
      )
    },
    []
  )

  const hasItemsForCategory = useCallback(
    (subSubCategory: string): boolean => {
      return itemsData.has(subSubCategory)
    },
    [itemsData]
  )

  const hasTrendItemsForCategory = useCallback(
    (subSubCategory: string): boolean => {
      return trendItemsData.has(subSubCategory)
    },
    [trendItemsData]
  )

  const getFormattedItemsForCategory = useCallback(
    (
      subSubCategory: string
    ): Array<IAbstractLensItem<any> & { parentName?: string }> => {
      const items = itemsData.get(subSubCategory) || []
      const trendItems = trendItemsData.get(subSubCategory) || []

      // Create a map of trend data by item name for easy lookup
      const trendDataMap = new Map(
        trendItems.map((item) => [item.itemName, item])
      )

      return items.map(
        (
          item: SalesLensItemsModel
        ): IAbstractLensItem<any> & { parentName?: string } => {
          const trendItem = trendDataMap.get(item.itemName)

          return {
            item: item.itemName,
            parentName: subSubCategory,
            current: {
              netSalesAmount: item.netSalesAmount,
              netSalesPercentage: item.netSalesPercentage,
              unitSold: item.unitSold,
              avgSellingPrice: item.avgSellingPrice,
              grossToDiscountPercentage: item.grossToDiscountPercentage,
            },
            prev: trendItem
              ? {
                  netSalesAmount: trendItem.netSalesAmount,
                  netSalesPercentage: trendItem.netSalesPercentage,
                  unitSold: trendItem.unitSold,
                  avgSellingPrice: trendItem.avgSellingPrice,
                  grossToDiscountPercentage:
                    trendItem.grossToDiscountPercentage,
                }
              : undefined,
          }
        }
      )
    },
    [itemsData, trendItemsData]
  )

  const clearCache = useCallback(() => {
    setItemsData(new Map())
    setTrendItemsData(new Map())
    setError(null)
  }, [])

  const refetch = useCallback(() => {
    console.log('Refetching sales lens items (clearing cache)...')
    clearCache()
  }, [clearCache])

  return {
    fetchItemsForCategory,
    hasItemsForCategory,
    hasTrendItemsForCategory,
    getFormattedItemsForCategory,
    clearCache,
    refetch,
    loading,
    error,
  }
}

async function fetchItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  subSubCategory: string
): Promise<{
  data: SalesLensItemsModel[]
}> {
  try {
    const response = await getSalesLensItems(
      branches,
      startDate,
      endDate,
      subSubCategory
    )

    const data: SalesLensItemsModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.item_name,
        responseItem.sub_sub_category,
        responseItem.net_sales_amount,
        responseItem.sales_percentage,
        responseItem.quantity_sold,
        responseItem.gross_to_discount_percentage
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  itemName: string,
  subSubCategory: string,
  netSalesAmount: number,
  netSalesPercentage: number,
  quantitySold: number,
  grossToDiscountPercentage: number
): SalesLensItemsModel {
  return {
    itemName,
    subSubCategory,
    netSalesAmount: formatMetric(netSalesAmount, 2),
    netSalesPercentage: formatMetric(netSalesPercentage, 2),
    unitSold: formatMetric(quantitySold, 2),
    avgSellingPrice: formatMetric(netSalesAmount / quantitySold, 2),
    grossToDiscountPercentage: formatMetric(grossToDiscountPercentage, 2),
  }
}
